import {
	PointerEvent,
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import {
	ActionListItem,
	Button,
	DropOverActionList,
	DropOverMenu,
	FontIconName,
	IconButton,
	SvgIconValue
} from "client/Components";
import { ReactionKind } from "client/Api/Hub/common";
import { localize } from "client_legacy/ndaw/culture";
import {
	MediaQueryKind,
	useLongTouch,
	useWorkspaceMediaQuery,
	useIsTouchDevice,
	useDelayedEvent
} from "client/Hooks";
import {
	generateTrackingId,
	setTrackingId
} from "client/Misc/utils";
import styles from "./ReactionsButton.module.less";

export type ReactionsButtonProps = {
	onReactionClick: (reaction: ReactionKind) => void;
	userReaction: ReactionKind;
	bubbleClick?: boolean;
	kind?: "transparent-for-list" | "secondary";
};

export const ReactionsButton = (props: ReactionsButtonProps): ReactElement => {
	const {
		onReactionClick,
		userReaction,
		bubbleClick,
		kind = "transparent-for-list"
	} = props;

	const [isVisible, setIsVisible] = useState<boolean>(false);
	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const selectedReaction: ReactionItem = useMemo(
		(): ReactionItem => {
			const reactionButton: ReactionItem | undefined = reactions.find((reaction) => reaction.option === userReaction);

			return reactionButton ?? {
				text: localize("_Like"),
				icon: "thumbs-up",
				option: ReactionKind.Like
			};
		},
		[userReaction]
	);

	const [triggerShowPopup, cancelShowPopup] = useDelayedEvent(
		useCallback(
			() => setIsVisible(true),
			[]
		),
		150
	);

	const [triggerHidePopup, cancelHidePopup] = useDelayedEvent(
		useCallback(
			() => setIsVisible(false),
			[]
		),
		500
	);

	const handlePointerLeave = useCallback(
		(args: PointerEvent): void => {
			if (args.pointerType !== "mouse") {
				return;
			}

			triggerHidePopup();
			cancelShowPopup();
		},
		[cancelShowPopup, triggerHidePopup]
	);

	const handlePointerEnter = useCallback(
		(args: PointerEvent): void => {
			if (args.pointerType !== "mouse") {
				return;
			}

			triggerShowPopup();
			cancelHidePopup();
		},
		[cancelHidePopup, triggerShowPopup]
	);

	const longPressHandler = useCallback(
		(): void => {
			setIsVisible(true);
		},
		[]
	);

	const {
		onTouchStart,
		onTouchMove,
		onTouchEnd,
		isActive: isLongPressActive
	} = useLongTouch({
		handler: longPressHandler,
		timeoutInMs: 300
	});

	const handleSelectedReactionClick = (): void => {
		if (!isLongPressActive) {
			onReactionClick(selectedReaction.option);
		}
	};

	const reactionSelectedHandler = (reaction: ReactionKind): void => {
		onReactionClick(reaction);
		setIsVisible(false);
	};

	const closeDropOverHandler = useCallback(
		(): void => {
			if (!isLongPressActive) {
				setIsVisible(false);
			}
		},
		[isLongPressActive]
	);

	return (
		<div
			className={styles.root}
			onPointerEnter={handlePointerEnter}
			onPointerLeave={handlePointerLeave}
			ref={ref}
		>
			<Button
				kind={kind}
				trackingId="like"
				icon={selectedReaction.icon}
				text={selectedReaction.text}
				onClick={handleSelectedReactionClick}
				onTouchStart={onTouchStart}
				onTouchMove={onTouchMove}
				onTouchEnd={onTouchEnd}
				bubbleClick={bubbleClick}
			/>
			{isVisible && ref.current && (
				<ReactionsMenu
					target={ref.current}
					onClose={closeDropOverHandler}
					onReactionSelected={reactionSelectedHandler}
					trackingId={generateTrackingId("like", "__drop-over")}
					bubbleClick={bubbleClick}
				/>
			)}
		</div>
	);
};


type ReactionsMenuProps = {
	target: HTMLElement;
	onClose: () => void;
	onReactionSelected: (reaction: ReactionKind) => void;
	trackingId: string;
	bubbleClick?: boolean;
};

const ReactionsMenu = (props: ReactionsMenuProps): ReactElement => {
	const {
		target,
		onClose,
		onReactionSelected,
		trackingId,
		bubbleClick
	} = props;

	const [isHovered, setIsHovered] = useState<boolean>(false);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const isTouchDevice: boolean = useIsTouchDevice();

	const reactionSelectedHandler = (reaction: ReactionKind): void => {
		setIsHovered(false);
		onReactionSelected(reaction);
	};

	if (isTouchDevice && isMobile) {
		return (
			<DropOverActionList
				target={target}
				onClose={onClose}
				trackingId={trackingId}
				bubbleClick={bubbleClick}
				menuTitle={localize("_Reactions")}
			>
				{reactions.map((reaction) => (
					<ActionListItem
						key={reaction.option}
						text={reaction.text}
						icon={reaction.icon}
						onClick={() => reactionSelectedHandler(reaction.option)}
						trackingId={generateTrackingId("reaction-button", `|${reaction.option}`)}
					/>
				))}
			</DropOverActionList>
		);
	}

	return (
		<DropOverMenu
			target={target}
			hasNoPadding
			onClose={onClose}
			hasResponsiveDropOver={false}
			title={localize("_Reactions")}
			trackingId={generateTrackingId(trackingId, "__wrapper")}
		>
			<div
				{...setTrackingId(trackingId)}
				className={styles.reactionsList}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
			>
				{reactions.map((reaction) => (
					<IconButton
						key={reaction.option}
						kind="transparent-colored"
						hoverStyle={isHovered ? "inversed" : "default"}
						icon={reaction.icon}
						onClick={() => reactionSelectedHandler(reaction.option)}
						tooltip={reaction.text}
						trackingId={generateTrackingId("reaction-button", `|${reaction.option}`)}
						bubbleClick={bubbleClick}
					/>
				))}
			</div>
		</DropOverMenu>
	);
};
ReactionsMenu.displayName = "ReactionsMenu";

type ReactionItem = {
	text: string;
	icon: FontIconName | SvgIconValue;
	option: Exclude<ReactionKind, ReactionKind.NoReaction>;
};

const reactions: ReactionItem[] = [
	{
		text: localize("_Like"),
		icon: { kind: "svg", name: "like" },
		option: ReactionKind.Like
	},
	{
		text: localize("_Love"),
		icon: { kind: "svg", name: "love" },
		option: ReactionKind.Love
	},
	{
		text: localize("_Sad"),
		icon: { kind: "svg", name: "sad" },
		option: ReactionKind.Sad
	},
	{
		text: localize("_Celebrate"),
		icon: { kind: "svg", name: "celebrate" },
		option: ReactionKind.Celebrate
	},
	{
		text: localize("_Laugh"),
		icon: { kind: "svg", name: "laugh" },
		option: ReactionKind.Laugh
	},
	{
		text: localize("_Thank"),
		icon: { kind: "svg", name: "thank" },
		option: ReactionKind.Thank
	},
];